import type { GeneratedQuestion } from '../generators/math/types';
import type { LessonState } from '../lesson';
import { shuffleMcqOptions } from './option-shuffle';

/** Minimal SFX interface — decoupled from AudioContext for testability */
export interface QuizSfx {
  playCorrect(): void;
  playWrong(): void;
}

/** Build a real SFX player using Web Audio API */
function buildWebAudioSfx(): QuizSfx {
  try {
    const ctx = new AudioContext();
    function playTone(freqs: number[], durationMs: number): void {
      const totalSec = durationMs / 1000;
      const noteSec  = totalSec / freqs.length;
      freqs.forEach((freq, i) => {
        const osc  = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.frequency.value = freq;
        osc.connect(gain).connect(ctx.destination);
        const start = ctx.currentTime + i * noteSec;
        gain.gain.setValueAtTime(0.3, start);
        gain.gain.exponentialRampToValueAtTime(0.001, start + noteSec - 0.01);
        osc.start(start);
        osc.stop(start + noteSec);
      });
    }
    return {
      playCorrect: () => playTone([523, 659], 280), // C5→E5 rising
      playWrong:   () => playTone([330, 262], 300), // E4→C4 falling
    };
  } catch {
    return { playCorrect: () => {}, playWrong: () => {} };
  }
}

/** Null SFX implementation — used when proMode is ON or in tests. */
const NULL_SFX: QuizSfx = { playCorrect: () => {}, playWrong: () => {} };

interface OverlayOptions {
  container: HTMLElement;
  onAnswer: (answer: number | string) => void;
  /** Optional SFX — uses Web Audio if omitted; pass mock in tests */
  sfx?: QuizSfx;
  /**
   * v24 教學專業模式. When true:
   *   - SFX defaults to NULL_SFX (silent) unless an explicit sfx is passed
   *   - 答對 / 答錯 banner shrinks to neutral inline text (no colored pop)
   *   - Phase label "暖身/引導/挑戰 N/M" becomes "第 N 題 / 共 M 題"
   * Default false (legacy gamified behaviour preserved for tests / non-pro
   * callers). The lesson runner reads `isProMode()` and passes this in.
   */
  proMode?: boolean;
  /**
   * Option C 救火 fix 3/3 — per-overlay seed for MCQ option shuffle.
   *
   * If set, every MCQ render reshuffles its options via mulberry32(
   * hash(shuffleSeed + question.prompt + position.current)). Same seed +
   * same question replays to identical layout (reproducibility for tests
   * and for "重玩同卷"). 是非題 (kind:'fill' + isTrueFalseQuestion) NEVER
   * shuffles — ○/× 順序 follows 台灣考卷慣例.
   *
   * If undefined, options render in source order (legacy behaviour —
   * test compatibility, existing overlay tests assert options[0]→radio[0]).
   */
  shuffleSeed?: string | number;
}

export class QuizOverlay {
  private root: HTMLElement;
  private onAnswer: (answer: number | string) => void;
  private sfx: QuizSfx;
  private proMode: boolean;
  private shuffleSeed: string | number | undefined;
  /**
   * P06 (iPad Multi-touch): debounce flag that flips the moment ONE answer
   * fires, blocking every subsequent tap until the next `render()` call
   * resets it. Without this guard a kid two-finger-tapping on different
   * MCQ choices (or rapidly double-tapping the same choice while the 300ms
   * flash animation runs) would fire onAnswer twice and confuse the lesson
   * runner. See docs/GAME_TECHNIQUES_WIKI.md §P06.
   */
  private _answerCommitted: boolean = false;

  constructor(opts: OverlayOptions) {
    this.root = document.createElement('div');
    this.root.className = 'quiz-overlay';
    if (opts.proMode) this.root.classList.add('quiz-overlay--pro');
    opts.container.appendChild(this.root);
    this.onAnswer = opts.onAnswer;
    this.proMode = opts.proMode === true;
    this.shuffleSeed = opts.shuffleSeed;
    // proMode ON → default to NULL_SFX (silent); explicit sfx still wins (tests).
    this.sfx = opts.sfx ?? (this.proMode ? NULL_SFX : buildWebAudioSfx());
  }

  /** Show correct/wrong flash + SFX, then call onAnswer after 300ms animation */
  showFeedback(isCorrect: boolean, triggeredEl: HTMLElement, answer: number | string): void {
    // P06 multi-tap guard: ignore re-entrant taps after the first commit.
    if (this._answerCommitted) return;
    this._answerCommitted = true;
    const flashClass = isCorrect ? 'correct-flash' : 'wrong-flash';
    triggeredEl.classList.add(flashClass);
    if (isCorrect) {
      this.sfx.playCorrect();
      // v24 教學專業模式:正式版字小、無色塊背景、無 pop 動畫(只在意內容).
      // 遊戲化版本維持原本的大字綠 banner.
      if (this.proMode) this._showResultBanner('正確', '#2e7d32', /*pro=*/true);
      else this._showResultBanner('答對了!', '#2e7d32', false);
    } else {
      this.sfx.playWrong();
      if (this.proMode) this._showResultBanner('錯誤', '#c62828', /*pro=*/true);
      else this._showResultBanner('再試一次!', '#c62828', false);
    }
    // P06: visually disable every other choice so a second finger landing
    // late can't even hit a different button before the 300ms unwind.
    const choicesEl = this.root.querySelector('.q-options');
    if (choicesEl) {
      for (const lbl of Array.from(choicesEl.querySelectorAll('label'))) {
        const radio = lbl.querySelector('input[type=radio]') as HTMLInputElement | null;
        if (radio) radio.disabled = true;
        (lbl as HTMLElement).style.pointerEvents = 'none';
      }
    }
    setTimeout(() => {
      triggeredEl.classList.remove(flashClass);
      this.onAnswer(answer);
    }, 300);
  }

  private _showResultBanner(text: string, color: string, pro: boolean): void {
    const banner = document.createElement('div');
    banner.className = pro ? 'quiz-result-banner quiz-result-banner--pro' : 'quiz-result-banner';
    banner.textContent = text;
    if (pro) {
      // 專業模式:小字、無背景色塊、無動畫,色用 border-left 標示 (考卷風).
      banner.style.cssText = [
        'position:absolute', 'top:8px', 'right:8px',
        `color:${color}`, 'font-size:16px', 'font-weight:600',
        `border-left:3px solid ${color}`, 'padding:2px 10px',
        'background:rgba(255,255,255,0.04)',
        'z-index:999', 'pointer-events:none',
      ].join(';');
    } else {
      banner.style.cssText = [
        'position:absolute', 'top:50%', 'left:50%',
        'transform:translate(-50%,-50%)',
        `background:${color}`, 'color:#fff', 'font-size:28px',
        'font-weight:bold', 'padding:16px 32px', 'border-radius:12px',
        'z-index:999', 'pointer-events:none',
        'animation:quizBannerPop 0.3s ease-out forwards',
      ].join(';');
    }
    this.root.style.position = 'relative';
    this.root.appendChild(banner);
    setTimeout(() => banner.remove(), 280);
  }

  render(question: GeneratedQuestion, phase: LessonState, position: { current: number; total: number }): void {
    // P06 multi-tap guard: new question → re-enable answer commits.
    this._answerCommitted = false;
    if (phase === 'done') {
      this.root.innerHTML = '';
      return;
    }
    // v24: proMode swaps the gamified phase names (暖身/引導/挑戰) for plain
    // "第 N 題" numbering — feels like a school exam, not a quest. Tests still
    // assert the gamified labels because they construct overlays without
    // proMode (default false), so the legacy branch is preserved.
    const phaseLabel = ({ warmup: '暖身', guide: '引導', challenge: '挑戰' } as const)[phase];
    const headerHtml = this.proMode
      ? `第 ${position.current} 題 / 共 ${position.total} 題`
      : `${phaseLabel} ${position.current} / ${position.total}`;

    if (question.kind === 'mcq') {
      this.root.innerHTML = `
        <div class="card formal-page exam-question">
          <div class="q-number">${headerHtml}</div>
          <div class="q-prompt">${escapeHtml(question.prompt)}</div>
          <div class="q-options"></div>
        </div>
      `;
      const choicesEl = this.root.querySelector('.q-options')!;

      // Option C 救火 fix 3: seeded option shuffle.
      // - shuffleSeed unset → legacy order (preserves existing render tests).
      // - shuffleSeed set   → permute options + remap correctAnswer per render.
      //   We submit the ORIGINAL index to onAnswer so LessonController's
      //   `answer === q.correctAnswer` check stays correct without mutating
      //   the question (lesson re-uses currentQuestion across retries).
      const { displayOptions, displayToOriginal } = this.shuffleSeed !== undefined
        ? shuffleMcqOptions(question.options, `${this.shuffleSeed}|${question.prompt}|${position.current}`)
        : { displayOptions: question.options.slice(), displayToOriginal: question.options.map((_, i) => i) };

      displayOptions.forEach((opt, displayIdx) => {
        const label = document.createElement('label');
        const radio = document.createElement('input');
        radio.type  = 'radio';
        radio.name  = 'quiz-choice';
        const originalIdx = displayToOriginal[displayIdx];
        const isCorrect = originalIdx === question.correctAnswer;
        radio.addEventListener('change', () => {
          this.showFeedback(isCorrect, label, originalIdx);
        });
        label.appendChild(radio);
        label.appendChild(document.createTextNode(String(opt)));
        choicesEl.appendChild(label);
      });
    } else if (isTrueFalseQuestion(question)) {
      // 591 是非題 entries (kind=fill + explanation startsWith '是非題') —
      // prompt 只敘述沒問,所以給大 ○ / × 按鈕 + 「是非題」標籤,
      // kid 一看就知道判對錯 (boss bug fix). 提交字串 "○" or "×",
      // 對齊 dataset 的 correctAnswer 寫法.
      this.root.innerHTML = `
        <div class="card formal-page exam-question">
          <div class="q-number">${headerHtml}</div>
          <div class="q-tf-label">是非題</div>
          <div class="q-prompt">${escapeHtml(question.prompt)}</div>
          <div class="q-tf-options">
            <button type="button" class="q-tf-btn q-tf-true"  data-tf="○" aria-label="○ 正確">○</button>
            <button type="button" class="q-tf-btn q-tf-false" data-tf="×" aria-label="× 錯誤">×</button>
          </div>
        </div>
      `;
      const correctVal = question.kind === 'fill' ? String(question.correctAnswer).trim() : '';
      const btns = this.root.querySelectorAll<HTMLButtonElement>('.q-tf-btn');
      btns.forEach(btn => {
        btn.addEventListener('click', () => {
          const val = btn.dataset.tf ?? '';
          const isCorrect = val === correctVal;
          this.showFeedback(isCorrect, btn, val);
        });
      });
    } else if (question.kind === 'fill') {
      // fill: text input + 送出 button
      this.root.innerHTML = `
        <div class="card formal-page exam-question">
          <div class="q-number">${headerHtml}</div>
          <div class="q-prompt">${escapeHtml(question.prompt)}</div>
          <div class="eq-fill-row">
            <input type="text" class="eq-fill-input" autofocus />
            <button class="primary-btn" id="fill-submit">送出</button>
          </div>
        </div>
      `;
      const input = this.root.querySelector('.eq-fill-input') as HTMLInputElement;
      const card  = this.root.querySelector('.exam-question') as HTMLElement;
      const submit = (): void => {
        const val = input.value;
        const isCorrect = String(val).trim() === String(question.correctAnswer).trim();
        this.showFeedback(isCorrect, card, val);
      };
      this.root.querySelector('#fill-submit')!.addEventListener('click', submit);
      input.addEventListener('keydown', e => { if (e.key === 'Enter') submit(); });
    } else {
      // v1.1 worked-example: 遊戲端 overlay 不 render worked-example
      // (學習與遊戲分離 — worked-example 只在 learn-quiz 出現).
      // 安全 fallback: 顯示 prompt + 空,絕不流到 game scoring。
      this.root.innerHTML = `
        <div class="card formal-page exam-question">
          <div class="q-number">${headerHtml}</div>
          <div class="q-prompt">${escapeHtml(question.prompt)}</div>
        </div>
      `;
    }
  }

  destroy(): void {
    this.root.remove();
  }
}

function escapeHtml(s: string): string {
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}

/**
 * 591 是非題 entries 跨 25 dataset file 共用同一 identifier:
 *   kind === 'fill' && explanation startsWith '是非題'
 * (and correctAnswer is '○' or '×' — see public/data/*.json)
 *
 * Exposed for tests in tests/learn/quiz/true-false-overlay.test.ts and
 * tests/data/true-false-prompts.test.ts so the rule stays in one place.
 */
export function isTrueFalseQuestion(q: GeneratedQuestion): boolean {
  if (q.kind !== 'fill') return false;
  const exp = (q.explanation ?? '').trim();
  if (!exp.startsWith('是非題')) return false;
  const ans = String(q.correctAnswer ?? '').trim();
  return ans === '○' || ans === '×';
}
